import type { WalletDto } from '@iac/contracts';
import { plnAmount } from '@iac/contracts';
import { useState, type FormEvent } from 'react';
import { DataTable, Td, Tr } from '../../components/DataTable';
import { DeleteButton } from '../../components/DeleteButton';
import { Dialog } from '../../components/Dialog';
import { Field } from '../../components/Field';
import { IconPencilSquare, IconPlus } from '../../components/icons';
import { PageHeader } from '../../components/PageHeader';
import { useDict } from '../../i18n/dictionary';
import { usePageTitle } from '../../lib/page';
import {
  useCreateWallet,
  useDeleteWallet,
  useUpdateWallet,
  useWallets,
} from './api';
import { financesDict } from './i18n';

type WalletValues = { name: string; balance: string };

/** Adding and editing alike; `wallet` seeds the fields once. */
function WalletForm({
  wallet,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  wallet?: WalletDto;
  submitLabel: string;
  pending: boolean;
  onSubmit: (values: WalletValues) => void;
  onCancel: () => void;
}) {
  const t = useDict(financesDict);
  const [name, setName] = useState(wallet?.name ?? '');
  const [balance, setBalance] = useState(wallet?.balance ?? '');

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit({ name, balance });
  }

  return (
    <form onSubmit={submit} className="form-stack">
      <Field
        id="wallet-name"
        label={t.name}
        name="name"
        type="text"
        autoComplete="off"
        required
        maxLength={100}
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Field
        id="wallet-balance"
        label={t.balance}
        name="balance"
        type="text"
        inputMode="decimal"
        autoComplete="off"
        placeholder="0,00"
        value={balance}
        onChange={(e) => setBalance(e.target.value)}
      />
      <div className="form-actions">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          {t.cancel}
        </button>
        <button type="submit" className="btn-primary" disabled={pending}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

function WalletRow({
  wallet,
  onEdit,
}: {
  wallet: WalletDto;
  onEdit: () => void;
}) {
  const t = useDict(financesDict);
  const remove = useDeleteWallet();

  return (
    <Tr id={`wallet-row-${wallet.id}`}>
      <Td emphasis>{wallet.name}</Td>
      <Td numeric>{plnAmount(wallet.balance)}</Td>
      <Td>
        <div className="row-actions">
          <button
            type="button"
            className="btn-icon-ghost"
            aria-label={t.editWallet(wallet.name)}
            title={t.editWallet(wallet.name)}
            onClick={onEdit}
          >
            <IconPencilSquare />
          </button>
          <DeleteButton
            label={t.deleteWallet(wallet.name)}
            confirm={t.confirmDeleteWallet(wallet.name)}
            onConfirm={() => remove.mutate(wallet.id)}
          />
        </div>
      </Td>
    </Tr>
  );
}

export function Wallets() {
  const t = useDict(financesDict);
  usePageTitle(t.walletsTitle);
  const { data: wallets } = useWallets();
  const create = useCreateWallet();
  const update = useUpdateWallet();
  const [dialog, setDialog] = useState<
    null | { mode: 'add' } | { mode: 'edit'; wallet: WalletDto }
  >(null);
  const close = () => setDialog(null);
  const editing = dialog?.mode === 'edit' ? dialog.wallet : undefined;

  return (
    <>
      <PageHeader
        title={t.wallets}
        actions={
          <button
            type="button"
            className="btn-primary-icon"
            aria-label={t.addWallet}
            title={t.addWallet}
            onClick={() => setDialog({ mode: 'add' })}
          >
            <IconPlus />
          </button>
        }
      />

      <DataTable
        columns={[
          { label: t.name },
          { label: t.balance, align: 'center' },
          { align: 'center' },
        ]}
        empty={t.noWallets}
      >
        {wallets &&
          wallets.length > 0 &&
          wallets.map((wallet) => (
            <WalletRow
              key={wallet.id}
              wallet={wallet}
              onEdit={() => setDialog({ mode: 'edit', wallet })}
            />
          ))}
      </DataTable>

      <Dialog
        open={dialog !== null}
        title={editing ? t.editWallet(editing.name) : t.addWallet}
        onClose={close}
      >
        <WalletForm
          key={editing?.id ?? 'new'}
          wallet={editing}
          submitLabel={editing ? t.save : t.add}
          pending={editing ? update.isPending : create.isPending}
          onSubmit={(values) =>
            editing
              ? update.mutate(
                  { id: editing.id, ...values },
                  { onSuccess: close },
                )
              : create.mutate(values, { onSuccess: close })
          }
          onCancel={close}
        />
      </Dialog>
    </>
  );
}
